<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main>
      <PageHero
        eyebrow="Business divisions"
        title="Two divisions,"
        subtitle="one accountable team."
        lead="Depending on the scope, work is delivered by our Building and Civil Division, our Rope Access Services Division, or both working together on the same project."
      >
        <template #after-lead>
          <nav class="division-jump" aria-label="Jump to a division">
            <a href="#building-civil">
              <span>{{ buildingCivilDivision.number }}</span>
              {{ buildingCivilDivision.name }}
            </a>
            <a href="#rope-access">
              <span>{{ ropeAccessDivision.number }}</span>
              {{ ropeAccessDivision.name }}
            </a>
            <a href="#rope-access-capability">
              <span>{{ ropeAccessCapability.number }}</span>
              {{ ropeAccessCapability.name }}
            </a>
          </nav>
        </template>
      </PageHero>

      <!-- ============ 4.1 BUILDING AND CIVIL ============ -->
      <section id="building-civil" class="division shell">
        <div class="division-layout">
          <figure class="division-media">
            <img
              :src="divisionFeature.buildingCivil.src"
              :alt="divisionFeature.buildingCivil.alt"
              loading="lazy"
            />
            <figcaption>{{ divisionFeature.buildingCivil.caption }}</figcaption>
          </figure>

          <div class="division-copy">
            <p class="eyebrow">
              <span class="eyebrow-line"></span>
              {{ buildingCivilDivision.number }} — Division
            </p>

            <h2 class="display-2">{{ buildingCivilDivision.name }}</h2>

            <p v-for="paragraph in buildingCivilDivision.paragraphs" :key="paragraph" class="body-copy">
              {{ paragraph }}
            </p>

            <blockquote class="focus-quote">
              <Target />
              <p>{{ buildingCivilDivision.focus }}</p>
            </blockquote>

            <RouterLink class="text-link division-link" to="/our-work">
              See building and civil works on site
              <ArrowRight />
            </RouterLink>
          </div>
        </div>
      </section>

      <!-- ============ 4.2 ROPE ACCESS DIVISION ============ -->
      <section id="rope-access" class="division division-alt">
        <div class="shell">
          <div class="division-layout reversed">
            <div class="division-copy">
              <p class="eyebrow">
                <span class="eyebrow-line"></span>
                {{ ropeAccessDivision.number }} — Division
              </p>

              <h2 class="display-2">{{ ropeAccessDivision.name }}</h2>

              <p class="body-copy">{{ ropeAccessDivision.intro }}</p>
              <p class="body-copy">{{ ropeAccessDivision.techniquesLead }}</p>

              <ul class="works-list">
                <li v-for="work in ropeAccessDivision.works" :key="work">
                  <CircleCheckBig />
                  <span>{{ work }}</span>
                </li>
              </ul>

              <div class="division-notes">
                <p class="body-copy">{{ ropeAccessDivision.efficiency }}</p>
                <p class="body-copy">{{ ropeAccessDivision.obstruction }}</p>
              </div>

              <RouterLink class="text-link division-link" to="/our-work">
                See rope access works on site
                <ArrowRight />
              </RouterLink>
            </div>

            <figure class="division-media">
              <img
                :src="divisionFeature.ropeAccess.src"
                :alt="divisionFeature.ropeAccess.alt"
                loading="lazy"
              />
              <figcaption>{{ divisionFeature.ropeAccess.caption }}</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <!-- ============ 4.2.1 ROPE ACCESS CAPABILITY ============ -->
      <section id="rope-access-capability" class="capability">
        <div class="capability-texture" aria-hidden="true"></div>

        <div class="shell capability-inner">
          <div class="capability-head">
            <p class="eyebrow eyebrow-dark">
              <span class="eyebrow-line"></span>
              {{ ropeAccessCapability.number }} — {{ ropeAccessCapability.name }}
            </p>

            <h2 class="display-2">
              A flexible and efficient alternative
              <span>to scaffolding and staging.</span>
            </h2>

            <p class="capability-lead">{{ ropeAccessCapability.intro }}</p>
          </div>

          <div class="advantages">
            <p class="advantages-lead">{{ ropeAccessCapability.advantagesLead }}</p>

            <div class="advantage-grid">
              <article
                v-for="(advantage, index) in ropeAccessCapability.advantages"
                :key="advantage"
                class="advantage"
              >
                <component :is="advantageIcons[index]" />
                <strong>{{ advantage }}</strong>
              </article>
            </div>
          </div>

          <div class="capability-detail">
            <article class="detail-card">
              <span class="detail-label">Trained personnel</span>
              <p>{{ ropeAccessCapability.practice }}</p>
            </article>

            <article class="detail-card">
              <span class="detail-label">Systems and supervision</span>
              <p>{{ ropeAccessCapability.systems }}</p>
            </article>

            <article class="detail-card">
              <span class="detail-label">Temporary by design</span>
              <p>{{ ropeAccessCapability.temporary }}</p>
            </article>
          </div>
        </div>
      </section>

      <SectionCta
        eyebrow="Division enquiry"
        heading="Not sure which division suits your scope?"
        body="Send us the project details, photos and location. We will review the requirements and recommend a practical approach — including whether rope access or conventional access is the better fit."
      />
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { ArrowRight, Building2, CircleCheckBig, Layers, Sparkles, Target } from "lucide-vue-next";

import PageHero from "@/components/PageHero.vue";
import SectionCta from "@/components/SectionCta.vue";
import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";
import {
  buildingCivilDivision,
  ropeAccessCapability,
  ropeAccessDivision,
} from "@/content/company-content";
import { divisionFeature } from "@/content/gallery";

export default {
  name: "DivisionsPage",

  components: {
    ArrowRight,
    CircleCheckBig,
    PageHero,
    SectionCta,
    SiteFooter,
    StandardNavBar,
    Target,
  },

  data() {
    return {
      buildingCivilDivision,
      ropeAccessDivision,
      ropeAccessCapability,
      divisionFeature,
      advantageIcons: [Building2, Layers, Sparkles],
    };
  },
};
</script>

<style scoped>
/* ---------- hero jump nav ---------- */

.division-jump {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 34px;
}

.division-jump a {
  display: grid;
  gap: 10px;
  padding: 18px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: -0.02em;
  text-decoration: none;
  transition:
    border-color 180ms ease,
    transform 180ms ease;
}

.division-jump a:hover {
  transform: translateY(-2px);
  border-color: var(--blue);
}

.division-jump span {
  color: var(--blue);
  font-size: 11px;
  letter-spacing: 0.14em;
}

/* ---------- divisions ---------- */

.division {
  position: relative;
  z-index: 2;
  padding: 104px 0;
}

.division-alt {
  border-top: 1px solid rgba(11, 37, 69, 0.08);
  border-bottom: 1px solid rgba(11, 37, 69, 0.08);
  background: #f0f6fc;
}

.division-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
  gap: 60px;
  align-items: start;
}

.division-layout.reversed {
  grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
}

.division-media {
  position: sticky;
  top: 110px;
  margin: 0;
  border: 1px solid var(--line);
  background: var(--surface);
  box-shadow: var(--shadow-card);
  overflow: hidden;
}

.division-media img {
  display: block;
  width: 100%;
  height: 460px;
  object-fit: cover;
}

.division-media figcaption {
  padding: 16px 20px;
  border-top: 1px solid var(--line-soft);
  color: var(--muted-light);
  font-size: 11px;
  line-height: 1.6;
}

.division-copy .display-2 {
  margin-bottom: 26px;
}

.division-copy .body-copy {
  margin: 0 0 18px;
}

.works-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 24px;
  margin: 26px 0;
  padding: 26px;
  border: 1px solid var(--line);
  background: var(--surface);
  list-style: none;
}

.works-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.works-list svg {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  color: var(--blue);
}

.works-list span {
  color: var(--ink-soft);
  font-size: 14px;
  line-height: 1.55;
}

.division-notes .body-copy:last-child {
  margin-bottom: 0;
}

.focus-quote {
  display: grid;
  grid-template-columns: 26px 1fr;
  gap: 16px;
  margin: 30px 0 0;
  padding: 26px;
  border-left: 3px solid var(--blue);
  background: var(--surface);
  box-shadow: var(--shadow-card);
}

.focus-quote svg {
  width: 24px;
  height: 24px;
  margin-top: 2px;
  color: var(--blue);
}

.focus-quote p {
  margin: 0;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: 19px;
  line-height: 1.45;
  letter-spacing: -0.025em;
}

.division-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 26px;
  color: var(--blue);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

.division-link svg {
  width: 15px;
  height: 15px;
  transition: transform 180ms ease;
}

.division-link:hover svg {
  transform: translateX(3px);
}

/* ---------- 4.2.1 capability (dark) ---------- */

.capability {
  position: relative;
  z-index: 2;
  overflow: hidden;
  padding: 116px 0;
  background: var(--navy);
  color: #e6eefa;
}

.capability-texture {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: radial-gradient(circle at 76% 20%, black, transparent 64%);
}

.capability-inner {
  position: relative;
  z-index: 2;
}

.capability-head {
  max-width: 900px;
}

.eyebrow-dark {
  color: #7fa8d6;
}

.eyebrow-dark .eyebrow-line {
  background: var(--blue-light);
}

.capability :deep(.display-2) {
  color: #ffffff;
}

.capability :deep(.display-2 span) {
  color: #7794b5;
}

.capability-lead {
  max-width: 780px;
  margin: 26px 0 0;
  color: #a8c0da;
  font-size: 15.5px;
  line-height: 1.85;
}

.advantages {
  margin-top: 56px;
}

.advantages-lead {
  margin: 0 0 20px;
  color: #7fa8d6;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.advantage-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.advantage {
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 16px;
  min-height: 108px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  transition:
    border-color 200ms ease,
    background 200ms ease;
}

.advantage:hover {
  border-color: rgba(76, 155, 240, 0.45);
  background: rgba(76, 155, 240, 0.07);
}

.advantage svg {
  width: 22px;
  height: 22px;
  color: var(--blue-light);
}

.advantage strong {
  color: #ffffff;
  font-family: var(--font-display);
  font-size: 17px;
  line-height: 1.32;
  letter-spacing: -0.02em;
}

.capability-detail {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 14px;
}

.detail-card {
  padding: 28px;
  border: 1px solid rgba(76, 155, 240, 0.28);
  background: linear-gradient(160deg, rgba(76, 155, 240, 0.1), rgba(255, 255, 255, 0.02));
}

.detail-label {
  display: block;
  margin-bottom: 14px;
  color: #7fa8d6;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.detail-card p {
  margin: 0;
  color: #cfe0f2;
  font-size: 13.5px;
  line-height: 1.8;
}

/* ---------- responsive ---------- */

@media (max-width: 1080px) {
  .division-layout,
  .division-layout.reversed {
    grid-template-columns: 1fr;
    gap: 36px;
  }

  /* keep the photo above the copy on both divisions when stacked */
  .division-layout.reversed .division-media {
    order: -1;
  }

  .division-media {
    position: static;
  }

  .division-media img {
    height: 320px;
  }

  .advantage-grid,
  .capability-detail {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .division-jump {
    grid-template-columns: 1fr;
  }

  .division {
    padding: 76px 0;
  }

  .division-media img {
    height: 240px;
  }

  .works-list {
    grid-template-columns: 1fr;
    padding: 22px;
  }

  .focus-quote {
    padding: 22px;
  }

  .focus-quote p {
    font-size: 17px;
  }

  .capability {
    padding: 76px 0;
  }
}
</style>
